import { createContext, type ReactNode, useContext } from "react";

import {
  type FeatureFlagKey,
  featureFlagDescriptions,
  featureFlags as staticFeatureFlags,
} from "shell/platform/config/featureFlags";

import type { FeatureFlags, FeatureFlagsContextType } from "./types";

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

const flags: FeatureFlags = Object.entries(staticFeatureFlags).reduce(
  (acc, [name, value]) => {
    acc[name] = {
      value: Boolean(value),
      description: featureFlagDescriptions[name as FeatureFlagKey] ?? "",
    };
    return acc;
  },
  {} as FeatureFlags,
);

const contextValue: FeatureFlagsContextType = {
  flags,
  hasFlagEnabled: (key: string) => Boolean(flags[key]?.value),
};

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => (
  <FeatureFlagsContext.Provider value={contextValue}>
    {children}
  </FeatureFlagsContext.Provider>
);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
    );
  }
  return context;
};
